import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import NewsletterService from '../../services/NewsletterService';
import Modal from '../ModalConversion';

/**
 * Vista previa del boletin de novedades: la lista de titulos tal y como va al
 * PDF, agrupada por categoria y **sin portada**, mas un boton para descargar el
 * PDF de ese mismo canal.
 *
 * El contenido y el PDF salen del mismo calculo del backend (filtro por canal,
 * categorias de la configuracion y reparto por cuenta), asi que lo que se ve
 * aqui es exactamente lo que se envia.
 *
 * Colores tomados de la maqueta del PDF: azul petroleo para los titulos de
 * seccion (#333E48) y verde de marca para el delimitador (#7EBB00).
 */

/**
 * Explica por que la lista sale vacia.
 *
 * El backend devuelve `stats` con cuantos titulos sobrevivieron a cada filtro,
 * asi que el aviso puede senalar el paso que fallo en vez de culpar siempre al
 * canal (que no siempre es el culpable: tambien puede ser el periodo o las
 * categorias configuradas).
 */
function emptyReason(data, channel) {
  const s = (data && data.stats) || {};
  if (s.en_periodo === 0) {
    return 'No hay titulos nuevos cargados en el periodo configurado.';
  }
  if (s.tras_categorias === 0) {
    return 'Hay titulos en el periodo, pero ninguno cae en las categorias configuradas.';
  }
  if (s.tras_canal === 0) {
    return `Hay ${s.tras_categorias} titulo(s) en el periodo, pero ninguno esta activo en el canal «${channel}».`;
  }
  return `No hay titulos nuevos de este periodo para el canal «${channel}».`;
}

export function NewsletterPreviewList({ isOpen, config, channel, channels = [], onChannelChange, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !channel) return;
    let cancelado = false;
    setLoading(true);
    setError('');
    setData(null);
    (async () => {
      try {
        const res = await NewsletterService.previewList({ ...config, channel_alias: channel });
        if (!cancelado) setData(res.data || null);
      } catch (e) {
        if (!cancelado) setError(e.message);
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => { cancelado = true; };
  }, [isOpen, channel, config]);

  // El payload del PDF lleva el canal para que el archivo descargado sea el mismo
  // que se esta viendo, no el boletin generico de todos los canales.
  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '_').slice(0, 15);
      await NewsletterService.downloadPreviewPdf(
        { ...config, channel_alias: channel },
        `novedades_${channel}_${stamp}.pdf`,
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const vacio = !loading && !error && data && data.total === 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="text-white mb-1">Vista previa</h5>
          <div className="d-flex align-items-center gap-2 mt-1">
            <small className="text-white-50">Canal</small>
            <select
              className="form-select form-select-sm bg-dark text-white border-secondary"
              style={{ width: 'auto' }}
              value={channel || ''}
              onChange={(e) => onChannelChange && onChannelChange(e.target.value)}
              disabled={loading || downloading}
            >
              {channels.map(c => (
                <option key={c.channel_alias} value={c.channel_alias}>
                  {c.channel_name || c.channel_alias}
                </option>
              ))}
            </select>
          </div>
          {data && (
            <small className="text-white-50 d-block mt-1">
              {data.period_from} → {data.period_to} · {data.total} título(s) ·{' '}
              {data.accounts.length} cuenta(s)
            </small>
          )}
        </div>
        <button
          className="btn btn-ink-outline"
          onClick={handleDownload}
          disabled={loading || downloading || !data || data.total === 0}
        >
          {downloading
            ? <><FontAwesomeIcon icon={faSpinner} spin className="me-1" /> Generando...</>
            : <><FontAwesomeIcon icon={faFilePdf} className="me-1" /> Descargar PDF</>}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center py-2">
          <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
          <small>{error}</small>
        </div>
      )}

      {loading && (
        <div className="text-center py-5 text-white-50">
          <FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Calculando títulos del periodo...
        </div>
      )}

      {vacio && (
        <div className="alert alert-warning py-2">
          <small>{emptyReason(data, channel)}</small>
        </div>
      )}

      {data && data.groups.map(group => (
        <section key={group.category} className="mb-4">
          <div
            className="d-flex align-items-center mb-2"
            style={{ borderBottom: '1px solid #7EBB00', paddingBottom: '6px' }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '8px', height: '8px', background: '#7EBB00',
                display: 'inline-block', marginRight: '10px', flex: '0 0 auto',
              }}
            />
            <h6 className="mb-0 fw-bold text-uppercase" style={{ color: '#7EA0B8', letterSpacing: '0.04em' }}>
              {group.category}
            </h6>
          </div>

          {group.titles.map((t, i) => (
            <div
              key={`${t.identifier || t.title}-${i}`}
              className="py-2"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div className="fw-bold text-white">{t.title}</div>
              {t.authors.length > 0 && (
                <div className="text-white-50 small">{t.authors.join(', ')}</div>
              )}
              <div className="text-white-50 small mt-1">
                {t.identifier && <span className="me-3">{t.identifier}</span>}
                {t.publisher && <span className="me-3">{t.publisher}</span>}
                {t.bisac.length > 0 && <span style={{ fontStyle: 'italic' }}>{t.bisac.join(', ')}</span>}
              </div>
              {t.description && (
                <div className="text-white-50 small mt-1" style={{ lineHeight: '1.45' }}>
                  {t.description}
                </div>
              )}
            </div>
          ))}
        </section>
      ))}
    </Modal>
  );
}

export default NewsletterPreviewList;